/**
 * `Account.GetAccountType` as the order screens read it (MV-27).
 *
 * keyon answered `providerAssignment.accounttype` in its spaced English
 * ('Registered Nurse', 'Administrator') and `professionalLicense.license` with
 * the account's own licence, which for a Provider or an Administrator was the
 * NPI main's profile wrote there. The port's bridge answers both from `/me`:
 * the account type in main's words again (MX-14; the API's slug rides beside
 * it as `account_type`), and the licences as records, the NPI among them
 * (api2/bridgeMap.js). The order screens still read the answer through these
 * two, which accept either spelling.
 */

/** `registered_nurse` -> `Registered Nurse`; main's own labels pass through. */
export function legacyAccountType(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';

  return text
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * The NPI on file for the signed-in person, '' when there is none. Main's
 * "Administrators without an NPI can't write or assign Orders" reads this;
 * a state licence is not an NPI.
 */
export function npiFromAccountType(professionalLicense) {
  const records = (professionalLicense && Array.isArray(professionalLicense.records))
    ? professionalLicense.records
    : [];
  const npi = records.find((row) => row && String(row.licensetype || '').toLowerCase() === 'npi');

  return npi ? String(npi.license || npi.licensenum || '').trim() : '';
}

/**
 * Any licence on file, '' when there is none: main's `professionalLicense.license`
 * was the account's own `proffesionalLicense`, whatever kind it held — a state
 * licence, or the NPI main's profile wrote there for a Provider or an
 * Administrator. The bridge answers the state (or other non-NPI) licence in
 * `license` and every licence in `records`.
 */
export function licenceOnFile(professionalLicense) {
  const pl = professionalLicense || {};
  const own = String(pl.license ?? '').trim();

  return own || npiFromAccountType(pl);
}

/**
 * Whether OrderPopup offers the "assign to an account provider" chooser, as
 * main's order writers see it (D6: a capability main's users have).
 *
 * Main wrote `accountType === "Administrator" == licence == ""`. Equality
 * chains left to right, so it reads `((type is Administrator) == licence) == ""`,
 * which is true for EVERY Administrator (with or without a licence) and for
 * anyone else who has a licence on file (a Provider, a Licensed Practical
 * Nurse, an allied professional), and false only for someone else whose
 * licence field is empty. Main 87010d45 said it meant "Administrators with
 * No NPI can assign orders to Account Providers"; narrowing the chooser to
 * that would take it from the others, and no recorded decision does, so this
 * is what main's users get. A Registered Nurse is offered it regardless, by
 * OrderPopup's own `ifNurseAccount`.
 *
 * "No licence on file" reads as main's '', not main's NULL. Main's column
 * defaults to NULL (and keyon answers null when there is no row), and the
 * chain is true for null whatever the role, so on main a non-Administrator
 * whose licence was never written IS offered the chooser. The port cannot
 * tell the two apart, as neither leaves a licence record. Main's own Add User
 * writes '' for a blank licence (AddUserForm `licenseNum || npi || ""`), so
 * '' is what every account main creates today holds; the port differs from
 * main only for a row Add User never wrote (an import, an older row), and
 * that is reported as a decision rather than guessed.
 *
 * @param {string} accountType main's words (legacyAccountType)
 * @param {string} licence licenceOnFile
 */
export function offersPrimaryPhysicianChooser(accountType, licence) {
  return accountType === 'Administrator' || String(licence ?? '').trim() !== '';
}
